import { useQuery, useMutation } from "react-query"
import qs from "query-string"
import toast from "react-hot-toast"

import { apiRequest } from "util/request"
import { queryClient } from "./QueryClientProvider"

/** ------------------- **/
/*  Users
/** ------------------- **/

export function useCurrentUser() {
  const cacheKey = ["user"]
  const query = () => apiRequest("user")
  return useQuery(cacheKey, query)
}

export function useUpdateConfirmUser() {
  return useMutation(({ confirmed }) =>
    apiRequest(`user`, "PUT", { confirmed })
  )
}

export function useUpdateUser() {
  return useMutation(
    ({
      emailReportCompetitorReport = undefined,
      emailReportCompetitorNewProducts = undefined,
      emailReportCompetitorSoldOutProducts = undefined,
      emailEventRestockProduct = undefined,
      emailEventLiquidateProduct = undefined,
      slackEventCompetitorNewProducts = undefined,
      slackEventCompetitorRestockedProducts = undefined,
      slackEventCompetitorSoldOutProducts = undefined,
      slackEventRestockProduct = undefined,
      slackEventLiquidateProduct = undefined,
    }) =>
      apiRequest(`user`, "PUT", {
        email_sub: !!emailReportCompetitorReport,
        email_report_competitor_report: emailReportCompetitorReport,
        email_report_competitor_new_products: emailReportCompetitorNewProducts,
        email_report_competitor_sold_out_products:
          emailReportCompetitorSoldOutProducts,
        email_event_restock_product: emailEventRestockProduct,
        email_event_liquidate_product: emailEventLiquidateProduct,
        slack_event_competitor_new_products: slackEventCompetitorNewProducts,
        slack_event_competitor_restocked_products:
          slackEventCompetitorRestockedProducts,
        slack_event_competitor_sold_out_products:
          slackEventCompetitorSoldOutProducts,
        slack_event_restock_product: slackEventRestockProduct,
        slack_event_liquidate_product: slackEventLiquidateProduct,
      }),
    {
      onSuccess: () => {
        toast.success("Account updated successfully")
        queryClient.invalidateQueries(["user"])
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue updating your profile")
      },
    }
  )
}

export function useGenerateApiKey() {
  return useMutation(
    () => {
      return apiRequest(`user/apitoken/get_token`, "POST")
    },
    {
      onSuccess: () => {
        toast.success("API key successfully generated")
        queryClient.invalidateQueries(["user"])
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue generating a key")
      },
    }
  )
}

export function useRevokeApiKey() {
  return useMutation(
    key => {
      return apiRequest(`user/apitoken/revoke_token?api_token=${key}`, "DELETE")
    },
    {
      onSuccess: () => {
        toast.success("API key revoked")
        queryClient.invalidateQueries(["user"])
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue revoking this key")
      },
    }
  )
}

export function useLogUserSession() {
  return useMutation(() => {
    return apiRequest(`user/log`, "POST")
  })
}

export function useGenerateReport() {
  return useMutation(
    ({ companyId, groupId, userId, startDate, endDate }) => {
      const paramString = qs.stringify({
        company_id: companyId,
        group_id: groupId,
        user_id: userId,
        start_date: startDate,
        end_date: endDate,
      })
      return apiRequest(`companies/${companyId}/company_export?${paramString}`)
    },
    {
      onSuccess: () => {
        toast.success("Your report has started being processed")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue generating that report")
      },
    }
  )
}

export function useListReports({ companyId, groupId, userId }) {
  const cacheKey = ["listReports", companyId, groupId, userId]
  const paramString = qs.stringify({
    company_id: companyId,
    group_id: groupId,
    user_id: userId,
  })
  const query = () => apiRequest(`user/list_company_reports?${paramString}`)
  return useQuery(cacheKey, query, {
    enabled: !!companyId && !!groupId && !!userId,
  })
}

export function useDownloadReport() {
  return useMutation(
    ({ companyId, groupId, userId, startDate, endDate }) => {
      const paramString = qs.stringify({
        company_id: companyId,
        group_id: groupId,
        user_id: userId,
        start_date: startDate,
        end_date: endDate,
      })
      return apiRequest(`user/download_company_report?${paramString}`)
    },
    {
      onSuccess: () => {
        toast.success("This report was successfully downloaded")
      },
      onError: error => {
        console.error(error)
        toast.error("Hmm, there was an issue downloading that report")
      },
    }
  )
}
